import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import Badge from '../../../components/ui/Badge';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  ACC_DOCUMENT_ATTACHMENT_ACCEPT,
  ACC_DOCUMENT_ATTACHMENT_HINT} from '../../../components/attachments/attachmentAccept';
import { EntityAttachmentsSection } from '../../../components/EntityAttachmentsSection';
import { EntityImageGallery } from '../../../components/EntityImageGallery';
import api from '../../../services/api';
import type { SpareItem, SpareItemAttachment } from './useData';

export const moduleConfig = {
  module: {
    title: 'Spare items',
    permissionModuleKey: 'spare_items',
    route: {
      list: '/spares/spare-items',
      new: '/spares/spare-items/new',
      detail: (id: number) => `/spares/spare-items/${id}`,
      edit: (id: number) => `/spares/spare-items/${id}/edit`},
    api: {
      list: '/spare-items',
      detail: (id: number) => `/spare-items/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by name, vendor code, drawing no, serial no or notes…' },
  filterGroups: [{ name: 'basic', label: 'Basic', icon: '', fields: ['name', 'isActive'] }],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'All names',
        getValue: (item: SpareItem) => item.name,
        options: []},
      {
        key: 'isActive',
        label: 'Active',
        type: 'boolean' as const,
        getValue: (item: SpareItem) => (item.isActive ? 'Yes' : 'No'),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ]},
    ],
    applyFilter: (items: SpareItem[], filters: Record<string, string[]>) => {
      return items.filter((item) => {
        if (filters.name?.length) {
          const v = item.name || '';
          if (!filters.name.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }
        if (filters.isActive?.length) {
          const s = item.isActive ? 'Yes' : 'No';
          if (!filters.isActive.includes(s)) return false;
        }
        return true;
      });
    }},
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New spare item',
        showTitle: false,
        variant: 'primary' as const,
        href: '/spares/spare-items/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: SpareItem) => item.id,
  },
  emptyStates: {
    noData: 'Create a spare item to get started.',
    noSearchResults: 'No spare items match your search.',
    noFilterResults: 'No spare items match the filters.',
  },
  delete: {
    title: 'Delete spare item',
    message: 'Delete this spare item?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete spare item.',
    handler: async (id: number) => {
      const { deleteSpareItem } = await import('../../../services/mutations');
      await deleteSpareItem(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This spare item does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Spare item',
    getTitle: (item: SpareItem) => item.name || `Spare item #${item.id}`},
  form: {
    new: { title: 'New spare item', subtitle: 'Create a spare catalog item', submitText: 'Create' },
    edit: { title: 'Edit spare item', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    supportsFiles: true,
    attachmentsLabel: 'Photos & files',
    attachmentsHint: `${ACC_DOCUMENT_ATTACHMENT_HINT}. Uploads are saved when you create or update the item.`,
    fileAccept: ACC_DOCUMENT_ATTACHMENT_ACCEPT,
    getViewFileUrl: (entityId: number, fileId: number) => `/spare-items/${entityId}/files/${fileId}`,
    create: async (data: Partial<SpareItem>, files?: File[]) => {
      const { createSpareItem } = await import('../../../services/mutations');
      return createSpareItem(data as Record<string, unknown>, files);
    },
    update: async (id: number, data: Partial<SpareItem>, files?: File[], filesToDetach?: number[]) => {
      const { updateSpareItem } = await import('../../../services/mutations');
      return updateSpareItem(id, data as Record<string, unknown>, files, filesToDetach);
    }},
  fields: [
    {
      name: 'libraryId',
      label: 'Library',
      type: 'select',
      placeholder: 'Select library',
      apiEndpoint: '/spare-libraries?page=1&limit=500',
      valueField: 'id',
      labelField: 'name',
      options: [{ value: '', label: 'No library' }]},
    {
      name: 'categoryId',
      label: 'Category',
      type: 'select',
      placeholder: 'Select category',
      apiEndpoint: '/spare-categories?page=1&limit=500',
      valueField: 'id',
      labelField: 'name',
      options: [{ value: '', label: 'No category' }]},
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Item name',
      validation: { required: true, minLength: 1, maxLength: 200 }},
    {
      name: 'vendorCode',
      label: 'Vendor code',
      type: 'text',
      placeholder: 'Optional vendor code',
      validation: { maxLength: 20 }},
    {
      name: 'drawingNo',
      label: 'Drawing no',
      type: 'text',
      placeholder: 'Optional drawing number',
      validation: { maxLength: 80 }},
    {
      name: 'serialNo',
      label: 'Serial no',
      type: 'text',
      placeholder: 'Optional serial number',
      validation: { maxLength: 80 }},
    { name: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Optional notes' },
    { name: 'stockMin', label: 'Stock min', type: 'number' },
    { name: 'stockMax', label: 'Stock max', type: 'number' },
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      switchLabel: 'Active',
      defaultValue: true},
    {
      name: 'isCritical',
      label: 'Critical',
      type: 'switch',
      switchLabel: 'Critical',
      defaultValue: false},
    {
      name: 'isHazardousMaterial',
      label: 'Hazardous material',
      type: 'switch',
      switchLabel: 'Hazardous material',
      defaultValue: false},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'main',
        title: 'Spare item',
        icon: '',
        gridCols: 'md:grid-cols-12',
        fields: [
          'libraryId',
          'categoryId',
          'name',
          'vendorCode',
          'drawingNo',
          'serialNo',
          'stockMin',
          'stockMax',
          'isActive',
          'isCritical',
          'isHazardousMaterial',
          'notes',
        ],
        fieldSpans: {
          libraryId: 'md:col-span-6',
          categoryId: 'md:col-span-6',
          name: 'md:col-span-8',
          vendorCode: 'md:col-span-4',
          drawingNo: 'md:col-span-6',
          serialNo: 'md:col-span-6',
          stockMin: 'md:col-span-6',
          stockMax: 'md:col-span-6',
          isActive: 'md:col-span-4',
          isCritical: 'md:col-span-4',
          isHazardousMaterial: 'md:col-span-4',
          notes: 'md:col-span-12'},
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<SpareItem>[] => [
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'vendorCode', header: 'Vendor code', sortable: true, render: (v) => <TextCell value={v} /> },
  {
    key: 'libraryId',
    header: 'Library',
    sortable: false,
    render: (_v, row) => <TextCell value={row?.library?.name} />},
  {
    key: 'categoryId',
    header: 'Category',
    sortable: false,
    render: (_v, row) => <TextCell value={row?.category?.name} />},
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v, row) => (
      <Badge variant={v ?? row?.isActive ? 'success' : 'danger'} className="shrink-0 border rounded-md px-2 py-0.5">
        {v ?? row?.isActive ? 'Yes' : 'No'}
      </Badge>
    )},
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

export const getDetailSections = (item: SpareItem, formatDate: (s?: string) => string) => {
  const files: SpareItemAttachment[] = item.FileAttachment ?? item.attachments ?? [];
  const imageFiles = files.filter((f) => (f.fileType || f.mimeType || '').toLowerCase().startsWith('image/'));

  const fetchImage = async (fileId: number) => {
    const response = await api.get(`/spare-items/${item.id}/files/${fileId}`, { responseType: 'blob' });
    return URL.createObjectURL(response.data);
  };

  return [
    {
      title: 'Overview',
      fields: [
        { label: 'Name', value: item.name },
        { label: 'Vendor code', value: item.vendorCode || '—' },
        { label: 'Library', value: item.library?.name || '—' },
        { label: 'Category', value: item.category?.name || '—' },
        { label: 'Drawing no', value: item.drawingNo || '—' },
        { label: 'Serial no', value: item.serialNo || '—' },
        { label: 'Stock min', value: item.stockMin != null ? String(item.stockMin) : '—' },
        { label: 'Stock max', value: item.stockMax != null ? String(item.stockMax) : '—' },
        { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
        { label: 'Critical', value: item.isCritical ? 'Yes' : 'No' },
        { label: 'Hazardous material', value: item.isHazardousMaterial ? 'Yes' : 'No' },
        { label: 'Notes', value: item.notes || '—' },
        { label: 'Created', value: formatDate(item.createdAt) },
        { label: 'Updated', value: formatDate(item.updatedAt) },
      ]},
    {
      title: 'Photos',
      condition: imageFiles.length > 0,
      content: (
        <EntityImageGallery
          files={imageFiles.map((f) => ({
            id: f.id,
            fileName: f.fileName,
            fileType: f.fileType || f.mimeType,
            fileSize: f.fileSize,
            uploadedAt: f.uploadedAt}))}
          getImageUrl={(fileId) => `/spare-items/${item.id}/files/${fileId}`}
          fetchImage={fetchImage}
        />
      )},
    {
      title: 'Files',
      content: (
        <EntityAttachmentsSection
          downloadEndpoint=""
          showBulkDownload={false}
          files={files.map((f) => ({
            id: f.id,
            fileName: f.fileName,
            fileSize: f.fileSize ?? null,
            fileType: f.fileType || f.mimeType || null,
            uploadedAt: f.uploadedAt ?? null}))}
          getViewFileUrl={(fileId) => `/spare-items/${item.id}/files/${fileId}`}
        />
      )},
  ];
};

export const getInitialFormData = (): Partial<SpareItem> => ({
  libraryId: undefined,
  categoryId: undefined,
  name: '',
  vendorCode: '',
  drawingNo: '',
  serialNo: '',
  notes: '',
  stockMin: undefined,
  stockMax: undefined,
  isActive: true,
  isCritical: false,
  isHazardousMaterial: false});

export const mapItemToFormData = (item: SpareItem): Partial<SpareItem> => ({
  libraryId: item.libraryId ?? undefined,
  categoryId: item.categoryId ?? undefined,
  name: item.name,
  vendorCode: item.vendorCode ?? '',
  drawingNo: item.drawingNo ?? '',
  serialNo: item.serialNo ?? '',
  notes: item.notes ?? '',
  stockMin: item.stockMin ?? undefined,
  stockMax: item.stockMax ?? undefined,
  isActive: item.isActive ?? true,
  isCritical: item.isCritical ?? false,
  isHazardousMaterial: item.isHazardousMaterial ?? false});

export type ModuleConfig = typeof moduleConfig;

const TableActionsCell: React.FC<{
  item: SpareItem;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => (
  <div className="flex items-center flex-nowrap">
    <Link
      to={moduleConfig.module.route.detail(item.id)}
      className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
      onClick={(e) => e.stopPropagation()}
      title="View"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    </Link>
    {canEdit && (
      <Link
        to={moduleConfig.module.route.edit(item.id)}
        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="Edit"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </Link>
    )}
    {canDelete && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    )}
  </div>
);

export const getTableActions = (
  item: SpareItem,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

export const getStatusBadges = () => null;
